import { useMutation, useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";

import { useCallback } from "react";

import { getLabStatusMeta } from "@/lib/lab-status";

import type { LabOrderListItem } from "@/types/lab";

import { markResultsViewed } from "@/api/provider/lab/lab";
import {
  adjustLabResultsReadyCountInCache,
  getLabOrderViewedAtFromCache,
  labOrderListKeyPrefix,
  labPatientOrderPagesKeyPrefix,
  labQueryKeys,
  setLabOrderViewedAtInCaches,
} from "@/api/provider/lab/lab-query";
import { markLabOrderNotificationsReadInCache } from "@/query/notifications/notifications";

interface OptimisticStamp {
  orderId: string;
  viewedAt: string;
  prevViewedAt: string | undefined;
}

/**
 * Opening a results-ready order clears the alert: optimistically stamp the
 * cached row so the chip/badge flip to "Completed" immediately, then persist.
 * The server only stamps once results are actually fetched, so if it comes
 * back unstamped (or the call fails) the alert is restored.
 */
export function useMarkLabResultsViewed(patientId: string) {
  const queryClient = useQueryClient();

  const getViewedAt = (orderId: string) =>
    getLabOrderViewedAtFromCache(patientId, orderId);
  const setViewedAt = (orderId: string, value: string | undefined) =>
    setLabOrderViewedAtInCaches(patientId, orderId, value);

  // Undo only while this call's stamp is still the cached value. A later open
  // or a realtime update owns the field, and the badge count, after that.
  const rollback = (stamp: OptimisticStamp) => {
    if (getViewedAt(stamp.orderId) !== stamp.viewedAt) return;
    setViewedAt(stamp.orderId, stamp.prevViewedAt);
    adjustLabResultsReadyCountInCache(1);
  };

  const { mutate } = useMutation({
    mutationFn: (order: LabOrderListItem) => markResultsViewed(order.id),
    onMutate: (order): OptimisticStamp => {
      const stamp = {
        orderId: order.id,
        viewedAt: new Date().toISOString(),
        prevViewedAt: getViewedAt(order.id),
      };
      setViewedAt(order.id, stamp.viewedAt);
      adjustLabResultsReadyCountInCache(-1);
      return stamp;
    },
    onSuccess: (updated, order, stamp) => {
      if (!updated?.resultsViewedAt) {
        rollback(stamp);
        return;
      }
      // Sync the detail cache too: the detail header derives "Results ready"
      // vs "Completed" from resultsViewedAt, and the authoritative POST
      // response would otherwise be dropped — leaving a stale badge when the
      // detail GET raced ahead or its server-side result fetch lagged.
      queryClient.setQueryData(labQueryKeys.order(order.id), updated);
      markLabOrderNotificationsReadInCache(order.id);
      posthog.capture("lab_order_results_viewed");
    },
    // Only a thrown request leaves the write's outcome unknown, so refetch too.
    onError: (_error, _order, stamp) => {
      if (stamp) rollback(stamp);
      queryClient.invalidateQueries({
        queryKey: labQueryKeys.orders(patientId),
      });
      queryClient.invalidateQueries({
        queryKey: [...labPatientOrderPagesKeyPrefix, patientId],
      });
      queryClient.invalidateQueries({ queryKey: labOrderListKeyPrefix });
      queryClient.invalidateQueries({
        queryKey: labQueryKeys.resultsReadyCount(),
      });
    },
  });

  return useCallback(
    (order: LabOrderListItem) => {
      if (getLabStatusMeta(order).category === "results_ready") mutate(order);
    },
    [mutate],
  );
}
